import {
  SearchByNameResult,
  SearchByOtherResultItem,
  SearchByPropertyResultItem,
} from './models';
import {
  SearchState,
  SearchDataActionTypes,
  SEARCH_LOADING,
  SEARCH_CLEAR,
  SEARCH_PROPERTY_ADD,
  SEARCH_NAME_ADD,
  SEARCH_OTHER_ADD,
  SEARCH_MULTI_ADD,
  SEARCH_IS_SELECTED_UPDATE,
  SEARCH_SHOW_REFERNCES_UPDATE,
  SEARCH_EXPORT_TYPE_UPDATE,
  SEARCH_TOGGLE_SELECT_ALL,
} from './Search.types';

const defaultState: SearchState = {
  propertyData: null,
  nameData: null,
  otherData: null,
  multiData: null,
  loading: false,
};

const updateIsSelectedInItems = (
  arr: any[],
  guid: string,
  isSelected: boolean,
) => {
  return arr.map((item: { guid: string }) =>
    item.guid === guid ? { ...item, isSelected } : item,
  );
};

const reduceSelectAllPropertyResults = (
  results: SearchByPropertyResultItem[],
  resultItemIndex: number,
  documentType: string,
  selectAll: boolean,
): SearchByPropertyResultItem[] => {
  // TODO validate documentType in order to be able to remove the ts-ignores in this method
  return results?.reduce(
    (acc: SearchByPropertyResultItem[], cur: SearchByPropertyResultItem) => {
      if (cur.index === resultItemIndex) {
        return [
          ...acc,
          {
            ...cur,
            [documentType]: {
              // @ts-ignore:
              ...cur[documentType],
              // @ts-ignore:
              items: cur[documentType].items.map((item) => ({
                ...item,
                isSelected: selectAll,
              })),
            },
          },
        ];
      }
      return [...acc, cur];
    },
    [],
  );
};

const reduceSelectAllNameResults = (
  results: SearchByNameResult[],
  resultItemIndex: number,
  documentType: string,
  selectAll: boolean,
): SearchByNameResult[] => {
  // TODO validate documentType in order to be able to remove the ts-ignores in this method
  return results.reduce(
    (acc: SearchByNameResult[], cur: SearchByNameResult) => {
      if (cur.index === resultItemIndex) {
        return [
          ...acc,
          {
            ...cur,
            [documentType]: {
              // @ts-ignore:
              ...cur[documentType],
              // @ts-ignore:
              items: cur[documentType].items.map((item) => ({
                ...item,
                isSelected: selectAll,
              })),
            },
          },
        ];
      }
      return [...acc, cur];
    },
    [],
  );
};

const reduceSelectAllOtherResults = (
  results: SearchByOtherResultItem[],
  resultItemIndex: number,
  documentType: string,
  selectAll: boolean,
): SearchByOtherResultItem[] => {
  return results?.reduce(
    (acc: SearchByOtherResultItem[], cur: SearchByOtherResultItem) => {
      if (cur.index === resultItemIndex && cur.searchBy === documentType) {
        return [
          ...acc,
          {
            ...cur,
            // @ts-ignore:
            items: cur.items.map((item) => ({
              ...item,
              isSelected: selectAll,
            })),
          },
        ];
      }
      return [...acc, cur];
    },
    [],
  );
};

const reduceIsSelectedPropertyResults = (
  results: SearchByPropertyResultItem[],
  resultItemIndex: number,
  documentType: string,
  guid: string,
  isSelected: boolean,
): SearchByPropertyResultItem[] => {
  // TODO validate documentType in order to be able to remove the ts-ignores in this method
  return results.reduce(
    (acc: SearchByPropertyResultItem[], cur: SearchByPropertyResultItem) => {
      if (cur.index === resultItemIndex) {
        return [
          ...acc,
          {
            ...cur,
            [documentType]: {
              // @ts-ignore:
              ...cur[documentType],
              ...(() => {
                switch (documentType) {
                  case 'recordedDocuments':
                  case 'liens': {
                    return {
                      items: updateIsSelectedInItems(
                        // @ts-ignore:
                        cur[documentType].items,
                        guid,
                        isSelected,
                      ),
                    };
                  }
                  case 'assessment': {
                    return { isSelected };
                  }
                }
              })(),
            },
          },
        ];
      }
      return [...acc, cur];
    },
    [],
  );
};

const reduceIsSelectedNameResults = (
  results: SearchByNameResult[],
  resultItemIndex: number,
  documentType: string,
  guid: string,
  isSelected: boolean,
): SearchByNameResult[] => {
  // TODO validate documentType in order to be able to remove the ts-ignores in this method
  return results.reduce(
    (acc: SearchByNameResult[], cur: SearchByNameResult) => {
      if (cur.index === resultItemIndex) {
        return [
          ...acc,
          {
            ...cur,
            [documentType]: {
              // @ts-ignore:
              ...cur[documentType],
              // @ts-ignore:
              items: updateIsSelectedInItems(
                // @ts-ignore:
                cur[documentType].items,
                guid,
                isSelected,
              ),
            },
          },
        ];
      }
      return [...acc, cur];
    },
    [],
  );
};

const reduceIsSelectedOtherResults = (
  results: SearchByOtherResultItem[],
  resultItemIndex: number,
  documentType: string,
  guid: string,
  isSelected: boolean,
): SearchByOtherResultItem[] => {
  // @ts-ignore:
  return results.reduce(
    // @ts-ignore:
    (acc: SearchByOtherResultItem[], cur: SearchByOtherResultItem) => {
      if (cur.index === resultItemIndex && cur.searchBy === documentType) {
        return [
          ...acc,
          {
            ...cur,
            items: updateIsSelectedInItems(cur.items, guid, isSelected),
          },
        ];
      }
      return [...acc, cur];
    },
    [],
  );
};

const reduceExportTypePropertyResults = (
  results: SearchByPropertyResultItem[] | undefined,
  resultItemIndex: number,
  exportType: string,
) => {
  return results?.reduce(
    (acc: SearchByPropertyResultItem[], cur: SearchByPropertyResultItem) => {
      if (cur.index === resultItemIndex) {
        return [
          ...acc,
          {
            ...cur,
            liens: {
              ...cur.liens,
              exportType,
            },
          },
        ];
      }
      return [...acc, cur];
    },
    [],
  );
};

const reduceExportTypeNameResults = (
  results: SearchByNameResult[] | undefined,
  resultItemIndex: number,
  exportType: string,
  documentType: string,
) => {
  // TODO validate documentType in order to be able to remove the ts-ignores in this method
  return results?.reduce(
    (acc: SearchByNameResult[], cur: SearchByNameResult) => {
      if (cur.index === resultItemIndex) {
        return [
          ...acc,
          {
            ...cur,
            [documentType]: {
              // @ts-ignore:
              ...cur[documentType],
              exportType,
            },
          },
        ];
      }
      return [...acc, cur];
    },
    [],
  );
};

const reduceShowReferencesPropertyResults = (
  results: SearchByPropertyResultItem[] | undefined,
  resultItemIndex: number,
  showReferences: boolean,
) => {
  return results?.reduce(
    (acc: SearchByPropertyResultItem[], cur: SearchByPropertyResultItem) => {
      if (cur.index === resultItemIndex) {
        return [
          ...acc,
          {
            ...cur,
            recordedDocuments: {
              ...cur.recordedDocuments,
              showReferences,
            },
          },
        ];
      }
      return [...acc, cur];
    },
    [],
  );
};

const reduceShowReferencesNameResults = (
  results: SearchByNameResult[] | undefined,
  resultItemIndex: number,
  showReferences: boolean,
  documentType: string,
) => {
  // TODO validate documentType in order to be able to remove the ts-ignores in this method
  return results?.reduce(
    (acc: SearchByNameResult[], cur: SearchByNameResult) => {
      if (cur.index === resultItemIndex) {
        return [
          ...acc,
          {
            ...cur,
            [documentType]: {
              // @ts-ignore:
              ...cur[documentType],
              showReferences,
            },
          },
        ];
      }
      return [...acc, cur];
    },
    [],
  );
};

export function searchReducer(
  state = defaultState,
  action: SearchDataActionTypes,
): SearchState {
  switch (action.type) {
    case SEARCH_TOGGLE_SELECT_ALL: {
      const {
        dataTypeName,
        documentType,
        selectAll,
        resultItemIndex,
        resultType,
      } = action.payload;

      switch (dataTypeName) {
        case 'propertyData': {
          return state?.propertyData?.results
            ? {
                ...state,
                propertyData: {
                  ...state.propertyData,
                  results: reduceSelectAllPropertyResults(
                    state?.propertyData?.results,
                    resultItemIndex,
                    documentType,
                    selectAll,
                  ),
                },
              }
            : { ...state };
        }
        case 'nameData': {
          return state?.nameData?.results
            ? {
                ...state,
                nameData: {
                  ...state.nameData,
                  results: reduceSelectAllNameResults(
                    state?.nameData?.results,
                    resultItemIndex,
                    documentType,
                    selectAll,
                  ),
                },
              }
            : { ...state };
        }
        case 'otherData': {
          return state.otherData
            ? {
                ...state,
                otherData: {
                  ...state.otherData,
                  results: reduceSelectAllOtherResults(
                    state.otherData.results,
                    resultItemIndex,
                    documentType,
                    selectAll,
                  ),
                },
              }
            : { ...state };
        }
        // @ts-ignore:
        case 'multiData': {
          switch (resultType) {
            case 'propertyResult': {
              return state?.multiData?.propertyResult?.results
                ? {
                    ...state,
                    multiData: {
                      ...state.multiData,
                      propertyResult: {
                        ...state.multiData.propertyResult,
                        results: reduceSelectAllPropertyResults(
                          state.multiData.propertyResult.results,
                          resultItemIndex,
                          documentType,
                          selectAll,
                        ),
                      },
                    },
                  }
                : { ...state };
            }
            case 'nameResult': {
              return state?.multiData?.nameResult?.results
                ? {
                    ...state,
                    multiData: {
                      ...state.multiData,
                      nameResult: {
                        ...state.multiData.nameResult,
                        results: reduceSelectAllNameResults(
                          state.multiData.nameResult.results,
                          resultItemIndex,
                          documentType,
                          selectAll,
                        ),
                      },
                    },
                  }
                : { ...state };
            }
            case 'otherResult': {
              return state?.multiData?.addressResult?.results
                ? {
                    ...state,
                    multiData: {
                      ...state.multiData,
                      addressResult: {
                        ...state.multiData.addressResult,
                        results: reduceSelectAllOtherResults(
                          state.multiData.addressResult.results,
                          resultItemIndex,
                          documentType,
                          selectAll,
                        ),
                      },
                    },
                  }
                : { ...state };
            }
          }
        }
        default: {
          return {
            ...state,
          };
        }
      }
    }
    case SEARCH_CLEAR: {
      return {
        ...defaultState,
      };
    }
    case SEARCH_LOADING: {
      return {
        ...state,
        loading: action.payload,
      };
    }
    case SEARCH_PROPERTY_ADD: {
      return {
        ...state,
        propertyData: action.payload,
      };
    }
    case SEARCH_IS_SELECTED_UPDATE: {
      const {
        dataTypeName,
        documentType,
        guid,
        isSelected,
        resultItemIndex,
        resultType,
      } = action.payload;
      const newData =
        dataTypeName &&
        state[dataTypeName] &&
        (() => {
          switch (dataTypeName) {
            case 'propertyData': {
              return state?.propertyData?.results
                ? {
                    ...state.propertyData,
                    results: reduceIsSelectedPropertyResults(
                      state.propertyData.results,
                      resultItemIndex,
                      documentType,
                      guid,
                      isSelected,
                    ),
                  }
                : { ...state.propertyData };
            }
            case 'otherData': {
              return state?.otherData?.results
                ? {
                    ...state.otherData,
                    results: reduceIsSelectedOtherResults(
                      state.otherData.results,
                      resultItemIndex,
                      documentType,
                      guid,
                      isSelected,
                    ),
                  }
                : { ...state.otherData };
            }
            case 'nameData': {
              return state?.nameData?.results
                ? {
                    ...state.nameData,
                    results: reduceIsSelectedNameResults(
                      state.nameData.results,
                      resultItemIndex,
                      documentType,
                      guid,
                      isSelected,
                    ),
                  }
                : { ...state.nameData };
            }
            case 'multiData': {
              switch (resultType) {
                case 'propertyResult': {
                  return state?.multiData?.propertyResult?.results
                    ? {
                        ...state.multiData,
                        propertyResult: {
                          ...state.multiData?.propertyResult,
                          results: reduceIsSelectedPropertyResults(
                            state?.multiData?.propertyResult?.results,
                            resultItemIndex,
                            documentType,
                            guid,
                            isSelected,
                          ),
                        },
                      }
                    : { ...state.multiData };
                }
                case 'nameResult': {
                  return state?.multiData?.nameResult?.results
                    ? {
                        ...state.multiData,
                        nameResult: {
                          ...state.multiData?.nameResult,
                          results: reduceIsSelectedNameResults(
                            state.multiData?.nameResult.results,
                            resultItemIndex,
                            documentType,
                            guid,
                            isSelected,
                          ),
                        },
                      }
                    : { ...state.multiData };
                }
                case 'otherResult': {
                  return state?.multiData?.addressResult?.results
                    ? {
                        ...state.multiData,
                        addressResult: {
                          ...state.multiData.addressResult,
                          results: reduceIsSelectedOtherResults(
                            state.multiData.addressResult.results,
                            resultItemIndex,
                            documentType,
                            guid,
                            isSelected,
                          ),
                        },
                      }
                    : { ...state.multiData };
                }
              }
            }
          }
        })();
      return newData
        ? {
            ...state,
            [dataTypeName]: newData,
          }
        : { ...state };
    }
    case SEARCH_EXPORT_TYPE_UPDATE: {
      const {
        dataTypeName,
        documentType,
        exportType,
        resultItemIndex,
        resultType,
      } = action.payload;
      const newData = (() => {
        switch (dataTypeName) {
          case 'propertyData': {
            const newResults = reduceExportTypePropertyResults(
              state?.propertyData?.results,
              resultItemIndex,
              exportType,
            );
            return {
              ...state.propertyData,
              results: newResults,
            };
          }
          case 'nameData': {
            const newResults = reduceExportTypeNameResults(
              state?.nameData?.results,
              resultItemIndex,
              exportType,
              documentType,
            );
            return {
              ...state[dataTypeName],
              results: newResults,
            };
          }
          case 'multiData': {
            switch (resultType) {
              case 'propertyResult': {
                const newResults = reduceExportTypePropertyResults(
                  state?.multiData?.propertyResult?.results,
                  resultItemIndex,
                  exportType,
                );
                return {
                  ...state.multiData,
                  propertyResult: {
                    ...state.multiData?.propertyResult,
                    results: newResults,
                  },
                };
              }
              case 'nameResult': {
                const newResults = reduceExportTypeNameResults(
                  state?.multiData?.nameResult?.results,
                  resultItemIndex,
                  exportType,
                  documentType,
                );
                return {
                  ...state.multiData,
                  nameResult: {
                    ...state.multiData?.nameResult,
                    results: newResults,
                  },
                };
              }
            }
          }
        }
      })();

      return newData
        ? {
            ...state,
            [dataTypeName]: newData,
          }
        : { ...state };
    }
    case SEARCH_SHOW_REFERNCES_UPDATE: {
      const {
        dataTypeName,
        documentType,
        showReferences,
        resultItemIndex,
        resultType,
      } = action.payload;

      const newData = (() => {
        switch (dataTypeName) {
          case 'propertyData': {
            const newResults = reduceShowReferencesPropertyResults(
              state?.propertyData?.results,
              resultItemIndex,
              showReferences,
            );
            return {
              ...state.propertyData,
              results: newResults,
            };
          }
          case 'nameData': {
            const newResults = reduceShowReferencesNameResults(
              state?.nameData?.results,
              resultItemIndex,
              showReferences,
              documentType,
            );
            return {
              ...state.nameData,
              results: newResults,
            };
          }
          case 'multiData': {
            switch (resultType) {
              case 'propertyResult': {
                const newResults = reduceShowReferencesPropertyResults(
                  state?.multiData?.propertyResult?.results,
                  resultItemIndex,
                  showReferences,
                );
                return {
                  ...state.multiData,
                  propertyResult: {
                    ...state.multiData?.propertyResult,
                    results: newResults,
                  },
                };
              }
              case 'nameResult': {
                const newResults = reduceShowReferencesNameResults(
                  state?.multiData?.nameResult?.results,
                  resultItemIndex,
                  showReferences,
                  documentType,
                );
                return {
                  ...state.multiData,
                  nameResult: {
                    ...state.multiData?.nameResult,
                    results: newResults,
                  },
                };
              }
            }
          }
        }
      })();

      return newData
        ? {
            ...state,
            [dataTypeName]: newData,
          }
        : { ...state };
    }

    case SEARCH_NAME_ADD: {
      return {
        ...state,
        nameData: action.payload,
      };
    }
    case SEARCH_OTHER_ADD: {
      return {
        ...state,
        otherData: action.payload,
      };
    }
    case SEARCH_MULTI_ADD: {
      return {
        ...state,
        multiData: action.payload,
      };
    }
    default:
      return state;
  }
}
